import { useCallback, useEffect, useState } from 'react';
import mapboxgl, { LngLatBoundsLike, LngLatLike } from 'mapbox-gl';
import { loadMapLayers } from '../loaders/MapLayersLoader';
import { loadMapControls } from '../loaders/MapControlsLoader';
import {
  // clustersEnabled,
  defaultLat,
  defaultLng,
  locationPermissionsLocalStorageKey,
  mapboxStyle,
  mapboxToken,
  // writeToGA4,
  writeToLog,
} from '../const';
import { useAppState } from '../core/appState/UseAppStateZustand';
import { useSearchBoxCore } from '@mapbox/search-js-react';
import Fuse from 'fuse.js';

interface MapInitializationProps {
  handleLongPressStart: (
    e: any,
    popupContainerRef: React.MutableRefObject<HTMLDivElement | null>,
  ) => void;
  handleLongPressEnd: () => void;
  handleMove: () => void;
  mapContainer: React.MutableRefObject<HTMLDivElement | null>;
  map: React.MutableRefObject<mapboxgl.Map | null>;
  popupContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  useClusters: boolean;
}

const useMapInitialization = ({
  handleLongPressEnd,
  handleLongPressStart,
  handleMove,
  map,
  mapContainer,
  popupContainerRef,
  useClusters,
}: MapInitializationProps) => {
  const {
    lng,
    lat,
    isMapInit,
    setIsMapInit,
    // setIsSettingsDialogOpen,
    // setIsHelpDialogOpen,
    setPreviousZoom,
    // setAutoCenter,
    setFlyingTo,
    zoom,
    setPartial,
    setMovingAutomatically,
  } = useAppState.getState();

  const [poi, setPoi] = useState<any>(null);

  const searchCore = useSearchBoxCore({
    accessToken: mapboxToken,
    proximity: poi?.geometry.coordinates,
    // poi_category: poi?.properties.class
  });

  //$Initialise the map
  const initMap = useCallback(
    (skipFlyTo?: boolean) => {
      if (isMapInit || map.current || !lat || !lng || isNaN(lat) || isNaN(lng))
        return;
      if (!mapContainer?.current) return;

      writeToLog('[useMapInitialization] running initMap');

      const queryParams = new URLSearchParams(window.location.search);
      // const initZoomLevel = queryParams.get("vz");
      // const viewportLat = queryParams.get("vlat");
      // const viewportLng = queryParams.get("vlng");
      const userLat = queryParams.get('lat');
      const userLng = queryParams.get('lng');
      const isSheetShowing = queryParams.get('s');
      // const skipLoadAnimation = queryParams.get("sk") ? true : false;
      const viewportMinLat = queryParams.get('vminlat');
      const viewportMinLng = queryParams.get('vminlng');
      const viewportMaxLat = queryParams.get('vmaxlat');
      const viewportMaxLng = queryParams.get('vmaxlng');

      let c: LngLatLike = [lng, lat];
      let z = 7;
      let b: LngLatBoundsLike = [0, 0, 0, 0];
      let useBounds: boolean = false;

      // if initZoomLevel + viewportLat + viewportLng, set map position
      if (
        viewportMinLat &&
        viewportMinLng &&
        viewportMaxLat &&
        viewportMaxLng
      ) {
        b = [
          parseFloat(viewportMinLng),
          parseFloat(viewportMinLat),
          parseFloat(viewportMaxLng),
          parseFloat(viewportMaxLat),
        ] as LngLatBoundsLike;
        useBounds = true;
      } else if (userLat && userLng) {
        c = [parseFloat(userLng), parseFloat(userLat)] as LngLatLike;
      }

      let mapOptions: any = {
        container: mapContainer.current,
        style: mapboxStyle,
      };

      if (useBounds) {
        mapOptions.bounds = b;
      } else {
        mapOptions.zoom = z;
        mapOptions.center = c;
      }

      if (isSheetShowing === 'true') {
        mapOptions = {
          ...mapOptions,
          dragPan: false,
          scrollZoom: false,
          doubleClickZoom: false,
          boxZoom: false,
          keyboard: false,
          touchZoomRotate: false,
          dragRotate: false,
        };
      }

      map.current = new mapboxgl.Map(mapOptions);

      let partial: any = {};

      map.current.on('load', () => {
        if (map.current) {
          partial.zoom = map.current.getZoom();
          const geoJson = {
            type: 'FeatureCollection',
            features: [],
          } as GeoJSON.FeatureCollection<GeoJSON.Geometry>;

          //initialise map layers
          loadMapLayers(
            map.current,
            geoJson,
            useClusters,
            zoom,
            // accuracySourceId
          );

          if (!skipFlyTo) {
            //check if we have any location details in local storage
            const homeString = localStorage.getItem(
              locationPermissionsLocalStorageKey,
            );

            if (homeString) {
              partial.permissionStatus = 'granted';
              const { hasUserMovedMap } = useAppState.getState();
              if (!hasUserMovedMap) {
                const loc = JSON.parse(homeString);
                writeToLog([
                  '[UseMapInitialization] [initMap] Flying home',
                  JSON.stringify(loc),
                ]);
                partial.flyingTo = true;
                map.current.flyTo({
                  center: [loc.lng, loc.lat],
                  zoom: 7,
                  speed: 3,
                  essential: true,
                });

                const handleMoveEnd = () => {
                  if (!map?.current) return;
                  setTimeout(() => {
                    setFlyingTo(false);
                    setMovingAutomatically(false);
                  }, 1000);
                  map.current.off('moveend', handleMoveEnd);
                };

                map.current.on('moveend', handleMoveEnd);
                partial.lng = loc.lng;
                partial.lat = loc.lat;
                partial.permissionStatus = loc.state;
              }
            } else {
              //move map to either the user's location or the default location
              partial.flyingTo = true;

              map.current.flyTo({
                center: [lng, lat],
                zoom: lat === defaultLat && lng === defaultLng ? 3 : 7,
                speed: 3,
                essential: true,
              });

              const handleMoveEnd = () => {
                if (!map?.current) return;
                setTimeout(() => {
                  setFlyingTo(false);
                  setMovingAutomatically(false);
                }, 400);
                map.current.off('moveend', handleMoveEnd);
              };

              map.current.on('moveend', handleMoveEnd);
            }
          }
          setPartial(partial);

          //Load the map controls and hide some others - includes zoom buttons and settings button
          loadMapControls(
            map.current, //,
            // () => {
            //   setIsSettingsDialogOpen(true);
            // },
            // () => {
            //   setIsHelpDialogOpen(true);
            // },
            // () => {
            //   const { autoCenter } = useAppState.getState();
            //   const { isRunningInWebView } = useAppState.getState();

            //   writeToGA4(
            //     isRunningInWebView ? "mobile_app" : "web_app",
            //     "autocenter_" + !autoCenter ? "on" : "off"
            //   );

            //   setAutoCenter(!autoCenter);
            // }
          );

          setPreviousZoom(map.current.getZoom());

          const handleLongPressStartWithParam = (e: any) =>
            handleLongPressStart(e, popupContainerRef);

          if (map.current) {
            map.current.on('click', () => {
              const { newMarker, setNewMarker } = useAppState.getState();

              // If there's an existing marker, remove it
              if (newMarker) {
                newMarker.remove();
                setNewMarker(null);
              }
            });
            map.current.on('move', handleMove);
            map.current.on('mousedown', handleLongPressStartWithParam);
            map.current.on('touchstart', handleLongPressStartWithParam);
            map.current.on('mouseup', handleLongPressEnd);
            map.current.on('touchend', handleLongPressEnd);
          }

          setIsMapInit(true);

          map.current.resize();

          setTimeout(() => {
            if (map.current) map.current.resize();
          }, 500);

          return () => {
            if (map.current) {
              map.current.off('move', handleMove);
              map.current.off('mousedown', handleLongPressStartWithParam);
              map.current.off('touchstart', handleLongPressStartWithParam);
              map.current.off('mouseup', handleLongPressEnd);
              map.current.off('touchend', handleLongPressEnd);
            }
          };
        }
      });

      map.current.on('click', (e) => {
        if (!map?.current) return;

        const features = map.current.queryRenderedFeatures(e.point, {
          layers: ['poi-label', 'transit-label', 'airport-label'],
        });

        if (!features.length) {
          return;
        }

        setPoi(features[0]);
      });
    },
    [
      handleLongPressEnd,
      handleLongPressStart,
      handleMove,
      map,
      mapContainer,
      // setAutoCenter,
      setFlyingTo,
      // setIsHelpDialogOpen,
      setIsMapInit,
      // setIsSettingsDialogOpen,
      setPreviousZoom,
      lat,
      lng,
      isMapInit,
      zoom,
      popupContainerRef,
      setPartial,
      setMovingAutomatically,
      useClusters,
    ],
  );

  useEffect(() => {
    const runSearch = async () => {
      if (!map?.current) return;
      if (poi) {
        try {
          const response = await searchCore.suggest(
            poi.properties.name_en ?? poi.properties.name,
            {
              sessionToken: window.location.href,
            },
          );
          const searchName = (poi.properties.name_en ?? poi.properties.name)
            .toLowerCase()
            .replace(/[\p{P}\p{S}]/gu, '');

          console.log(
            'suggs 1',
            response.suggestions,
            (poi.properties.name_en ?? poi.properties.name)
              .toLowerCase()
              .replace(/[\p{P}\p{S}]/gu, ''),
          );

          let f = response.suggestions.filter(
            (r) =>
              r.name.toLowerCase().replace(/[\p{P}\p{S}]/gu, '') === searchName && r.feature_type !== 'brand',
          );

          if (f.length === 0) f = response.suggestions.filter((r) => r.feature_type !== 'brand');

          if (f.length === 0) return;

          if (f.length > 1) {
            //find best match
            const options = {
              keys: ['name'],
              includeScore: true,
              threshold: 0.4,
            };

            const fuse = new Fuse(f, options);

            const results = fuse.search(
              poi.properties.name_en ?? poi.properties.name,
            );

            f = [results[0].item];
          }

          // Add a marker
          const { setNewMarker, newMarker } = useAppState.getState();

          if (newMarker) newMarker.remove();

          var el = document.createElement('div');
          el.id = 'marker';
          el.classList.add('marker');

          const marker = new mapboxgl.Marker(el)
            .setLngLat(poi.geometry.coordinates)
            .addTo(map.current);

          const newPopup = new mapboxgl.Popup({ offset: 25 });

          newPopup
            //@ts-ignore
            .setLngLat(poi.geometry.coordinates)
            .setHTML(
              '<h3 style="font-family: Rethink Sans;">' + f[0]?.name + '</h3>',
            )
            .addTo(map.current);

          marker.setPopup(newPopup);

          setNewMarker(marker);

          // pop location in app
          try {
            //@ts-ignore
            window.flutter_inappwebview.callHandler(
              'fromWebApp',
              'open_mapbox_location ' + f[0]?.mapbox_id,
            );
          } catch (e) {
            console.log('open_mapbox_location ' + f[0]?.mapbox_id);
          }
        } catch (error) {
          console.error('Search error:', error);
        }
      }
    };
    runSearch();
  }, [poi, searchCore, map]); // Dependency array ensures it runs when clickCoords or searchCore changes

  return initMap;
};

export default useMapInitialization;
